import {
  forwardRef,
  Fragment,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { MenuItem, Popover } from "@material-ui/core"
import { css, cx } from "@emotion/css"
import { getCursorPosition, interpolate, revertInterpolated } from "./utils"

import Button from "ui/buttons/Button"
import { ReactComponent as DeleteIcon } from "assets/clear_circle.svg"
import { ReactComponent as VectorIcon } from "assets/vector.svg"
import { useAuthenticated } from "providers/A12n"

type Props = {
  value: string
  match: RegExp
  options: Record<string, string>
  noOptions?: string
  endAdornment?: string
  clearAll?: string
  omitSelectedOptions?: boolean
  addBtnText: string
  onUpdate: (s: string) => void
  onChange?: (s: string) => void
  onBlur?: () => void
  onClear?: () => void
}

export const EditableInterpolatedText = forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { hasAuthority } = useAuthenticated()
    const localRef = useRef<HTMLDivElement | null>(null)
    const {
      value,
      match,
      options,
      noOptions,
      omitSelectedOptions,
      endAdornment,
      clearAll,
      addBtnText,
      onUpdate,
      onBlur,
      onClear,
    } = props
    const [cursorPosition, setCursorPosition] = useState<number>()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const availableOptionsKeys = omitSelectedOptions
      ? Object.keys(options).filter((option) => !value?.includes(option))
      : Object.keys(options)

    const open = Boolean(anchorEl)
    const { parts, remain } = interpolate(value, match, options)

    const onOpen = (event: ReactMouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const onClose = () => setAnchorEl(null)

    const onSelect = (k: string) => {
      const existing = (localRef.current?.innerText || "").trimEnd()

      let u = ""

      if (cursorPosition && cursorPosition < existing.length) {
        u = [
          existing.slice(0, cursorPosition).trimEnd(),
          `[${k}]`,
          existing.slice(cursorPosition, existing.length).trimStart(),
        ].join(" ")
      } else {
        u = [existing, `[${k}] `].join(" ")
      }
      onClose()
      onUpdate(u)
      setCursorPosition(u.length)
    }

    const onRemove = (idx: number = 0, replace: string) => {
      if (localRef.current?.innerText) {
        const existing = localRef.current?.innerText
        if (existing) {
          const r = revertInterpolated(existing, options)
          const u = [
            r.slice(0, idx),
            r.slice(idx + replace.length + 1, r.length),
          ].join("")
          onUpdate(u)
        }
      }
    }

    const onUpdateCursor = () => {
      const cursorPosition = getCursorPosition()
      setCursorPosition(cursorPosition)
    }

    const allowed =
      hasAuthority(["AUTH_EDIT_MARKET_TAX_EXEMPT"]) ||
      hasAuthority(["AUTH_MANAGE_CLIENT_TAXONOMY"])

    return (
      <div className={styles.container}>
        {allowed && (
          <div className={styles.toolbar}>
            <Button onClick={onOpen} weight="normal" variant="default">
              <span className="BodyBody-2Primary-mainBold_center">
                {addBtnText}
              </span>
            </Button>
            <Popover
              open={open}
              onClose={onClose}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <div className={styles.popover}>
                {!!availableOptionsKeys.length ? (
                  availableOptionsKeys?.map((opk) => (
                    <MenuItem key={opk} onClick={(_) => onSelect(opk)}>
                      {options[opk]}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <span>{noOptions}</span>
                  </MenuItem>
                )}
              </div>
            </Popover>
          </div>
        )}
        {allowed ? (
          <div className={styles.wrapper}>
            <div
              className={styles.content}
              key={value}
              contentEditable
              suppressContentEditableWarning={true}
              onBlur={onBlur}
              onKeyUp={(_) => onUpdateCursor()}
              onMouseUp={(_) => onUpdateCursor()}
              ref={(n) => {
                localRef.current = n
                if (typeof ref === "function") {
                  ref(n)
                } else if (ref) {
                  ref.current = n
                }
              }}
            >
              <div className={styles.chipsWrapper}>
                {parts.map((p, idx) => {
                  const { simple, interpolated, replace, index } = p

                  return (
                    <Fragment key={`parts_${idx}`}>
                      {simple.replace(replace, "")}
                      <span
                        className={cx(
                          "BodyBody-1Primary-mainRegular",
                          styles.interpolated,
                        )}
                        contentEditable="false"
                      >
                        {interpolated}
                        <DeleteIcon
                          onClick={() => onRemove(index, replace)}
                          className={styles.delete}
                        />
                      </span>
                    </Fragment>
                  )
                })}
                {remain}
              </div>
            </div>
            {!!endAdornment && (
              <div className={styles.uniqueNo} contentEditable="false">
                <div className={styles.divider} />
                <div className={cx("BodyBody-1Dark-greyRegular")}>
                  {endAdornment}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.content}>
            <div>
              {parts.map((p, idx) => {
                const { simple, interpolated, replace, index } = p
                return (
                  <Fragment key={`parts_${idx}`}>
                    {simple.replace(replace, "")}
                    <span
                      onClick={() => onRemove(index, interpolated)}
                      className={cx(
                        "BodyBody-1Primary-mainRegular",
                        styles.interpolated,
                      )}
                    >
                      {interpolated}
                    </span>
                  </Fragment>
                )
              })}
              {remain}
            </div>
            {!!endAdornment && (
              <div className={styles.uniqueNo} contentEditable="false">
                <div className={styles.divider} />
                <div className={cx("BodyBody-1Dark-greyRegular")}>
                  {endAdornment}
                </div>
              </div>
            )}
          </div>
        )}
        {!!clearAll && (
          <div className={styles.toolbar}>
            <Button className={styles.textButton} onClick={onClear}>
              <div className={cx("BodyBody-sBlackRegular", styles.btn)}>
                <div style={{ paddingRight: 4 }}>
                  <VectorIcon />
                </div>
                {clearAll}
              </div>
            </Button>
          </div>
        )}
      </div>
    )
  },
)

const styles = {
  container: css`
    max-width: 780px;
    display: flex;
    flex-direction: column;
  `,
  toolbar: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: end;
  `,
  content: css`
    display: flex;
    align-items: center;
    min-height: var(--spacing-xxl-4);
    justify-content: space-between;
    padding: 1rem;
    white-space: pre-wrap;
    outline: none;
  `,
  wrapper: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border: 1px solid var(--light-grey);
    border-radius: 4px;
    :focus-within {
      outline: 1px solid var(--light-grey);
    }
  `,
  interpolated: css`
    white-space: nowrap;
    line-height: 3;
    background-color: var(--primary-light);
    border-radius: 16px;
    padding: 7px var(--spacing-m) 6px;
    color: var(--primary-main);
  `,
  uniqueNo: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 77px;
    margin-right: var(--spacing-xxs);
  `,
  divider: css`
    margin: 0 var(--spacing-xxs);
    border: 1px solid var(--light-grey);
    height: var(--spacing-xxl);
  `,
  textButton: css`
    cursor: pointer;
    padding: var(--spacing-s) 0;
  `,
  btn: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  delete: css`
    padding: var(--spacing-xxs) 0 0 var(--spacing-xxs);
    margin-left: var(--spacing-xs);
  `,
  chipsWrapper: css`
    max-width: 450px;
  `,
  popover: css``,
}
